import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardBody, Col, Badge } from 'reactstrap'
import { H5, Image } from '../../AbstractElements'
import { getFile } from '../../lib/file'
import { getIdData } from '../../lib/studentinfo'
import { useLayout } from '../../Hooks/useLayout'
import { useRoleBasedUrl } from '../../Hooks/useRoleBasedUrl'

// Default avatar image
const emptyAvatar = require('../../assets/images/user/user-dp.png')

const StudentCard = ({
	data,
	showLinks = true,
	showEditIcon = true,
	cardSize = 'xl-4', // xl-4, xl-6, etc.
	className = 'col-ed-4 box-col-4',
	children // For custom actions/content from parent components
}) => {
	const { studentId, id } = data
	const [avatar, setAvatar] = useState()
	const [studentInfo, setStudentInfo] = useState(null)
	const [loading, setLoading] = useState(true)
	const layout = useLayout()
	const role = useRoleBasedUrl()

	useEffect(() => {
		const fetchStudentData = async () => {
			try {
				setLoading(true)
				// Use studentId if available, otherwise use id
				const studentData = await getIdData(studentId || id)

				if (studentData && studentData.length > 0) {
					const student = studentData[0]
					setStudentInfo(student)

					// Fetch avatar if available
					if (student.avatar_url) {
						try {
							const avatarData = await getFile(student.avatar_url)
							setAvatar(avatarData.publicUrl)
						} catch (error) {
							console.error('Error fetching avatar:', error)
							setAvatar(emptyAvatar)
						}
					} else {
						setAvatar(emptyAvatar)
					}
				}
			} catch (error) {
				console.error('Error fetching student data:', error)
			} finally {
				setLoading(false)
			}
		}

		if (studentId || id) {
			fetchStudentData()
		}
	}, [studentId, id])

	if (loading) {
		return (
			<Col xl={cardSize} sm="6" xxl="3" className={className}>
				<Card className="social-profile">
					<CardBody>
						<div className="text-center">
							<div className="spinner-border text-primary" role="status">
								<span className="sr-only">Loading...</span>
							</div>
						</div>
					</CardBody>
				</Card>
			</Col>
		)
	}

	if (!studentInfo) {
		return null
	}

	return (
		<Col xl={cardSize} sm="6" xxl="3" className={className}>
			<Card className="social-profile">
				<CardBody>
					<div className="social-img-wrap">
						<div className="social-img">
							{showLinks ? (
								<Link to={`${process.env.PUBLIC_URL}/${role}/student-tasks/${studentInfo.id}/${layout}`}>
									<Image
										attrImage={{
											src: avatar,
											alt: `${studentInfo.name} ${studentInfo.surname}`,
											className: 'img-fluid rounded-circle'
										}}
									/>
								</Link>
							) : (
								<Image
									attrImage={{
										src: avatar,
										alt: `${studentInfo.name} ${studentInfo.surname}`,
										className: 'img-fluid rounded-circle'
									}}
								/>
							)}
						</div>
						{showEditIcon && showLinks && (
							<div className="edit-icon">
								<Link to={`${process.env.PUBLIC_URL}/${role}/student-edit/${studentInfo.id}/${layout}`}>
									<i className="fa fa-edit"></i>
								</Link>
							</div>
						)}
					</div>
					<div className="social-details">
						<H5 attrH5={{ className: 'mb-1' }}>
							{showLinks ? (
								<Link to={`${process.env.PUBLIC_URL}/${role}/student-profile/${studentInfo.id}/${layout}`}>
									{studentInfo.name} {studentInfo.surname}
								</Link>
							) : (
								<>
									{studentInfo.name} {studentInfo.surname}
								</>
							)}
						</H5>
						<span className="f-light">{studentInfo.schoolname || 'School not specified'}</span>

						{/* Student Status */}
						<div className="mt-2">
							<Badge color={studentInfo.isActive ? 'success' : 'danger'} className="me-2">
								{studentInfo.isActive ? 'Active' : 'Inactive'}
							</Badge>
							<Badge color="info">Class: {studentInfo.class || 'N/A'}</Badge>
						</div>

						{/* Custom Actions/Content from parent components */}
						{children && <div className="mt-3">{children}</div>}
					</div>
				</CardBody>
			</Card>
		</Col>
	)
}

export { StudentCard }
